import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { PageContext } from '@blaze-shared/projects-domain';
import { PageContextSchema } from '@blaze-shared/schema-domain';

import { logPageContext } from './logger';
import { InitializePageContextService } from './services/initialize-page-context.service';
import { PageContextCommandService } from './services/page-context-command.service';
import { PageContextQueryService } from './services/page-context-query.service';
import {
  PageContextSourceData,
  PreparePageContextDataService,
} from './services/prepare-page-context-data.service';
@Injectable({
  providedIn: 'root',
})
export class PageContextFacade {
  private readonly initializePageContextService = inject(InitializePageContextService);
  private readonly pageContextForPageService = inject(PageContextQueryService);
  private readonly preparePageContextDataService = inject(PreparePageContextDataService);
  private readonly pageContextCommandService = inject(PageContextCommandService);

  /**
   * Populate page context state from session storage.
   * Used at initial project load.
   * This includes refreshing and when the page is opened in a new tab.
   * @param projectId - ID of the project (has to be passed as when initializing the project id is not in the state yet)
   */
  loadPageContextForProject(projectId: string): Observable<void> {
    return this.initializePageContextService.initializePageContextForProject(projectId);
  }

  /**
   * Generate a Page Context object from the provided data.
   */
  generatePageContext(pageContextSourceData: PageContextSourceData): PageContext {
    return this.preparePageContextDataService.preparePageContext(pageContextSourceData);
  }

  /**
   * Adds the page context to the state.
   * @param pageContext - Page context to set
   * @param originPageId - Optional origin page ID to set in the page context
   */
  setPageContext(pageContext: PageContext, originPageId: string): void {
    const originContextId = this.getCurrentPageContextIdSync();
    let preparedPageContext = pageContext;
    if (!pageContext.origin?.pageContextId && !pageContext.origin?.pageId) {
      preparedPageContext = {
        ...pageContext,
        origin: {
          pageId: originPageId,
          pageContextId: originContextId,
        },
      };
    }
    this.pageContextCommandService.setPageContext(preparedPageContext);
  }

  /**
   * Adds the page context to the state and removes other instances with the same page id
   * @param pageContext - Page context to set
   */
  setPageContextForOverlay(pageContext: PageContext): void {
    this.pageContextCommandService.setForOverlay(pageContext);
  }

  /**
   * Observes the Page Context for the given page ID.
   * This finds the currently active page context for the given page ID.
   * Works for the main page, dialog, drawer, header etc.
   * It combines the data from the History API (back/forward), url param.
   * Also, waits for the page context to be ready in case the data row has to be preloaded.
   */
  getPageContextForPage(pageId: string): Observable<PageContext | undefined> {
    return this.pageContextForPageService.getPageContextForPageId(pageId);
  }

  /**
   * Observes the Page Context schema for the given page ID.
   * Falls back to the page's main dataset schema when no concrete page context is loaded,
   * so `$$Page` intellisense works on Detail Pages even without a selected data row.
   */
  getPageContextSchemaForPage(pageId: string): Observable<PageContextSchema | undefined> {
    return this.pageContextForPageService.getPageContextSchemaForPageId(pageId);
  }

  /**
   * Returns the page context for the given page ID.
   * This works synchronously which means that if the page context is not ready yet,
   * it will return undefined even if it is going to change in a split second.
   *
   * **NOTE:** `observePageContextForPage` is strongly recommended even if just a single value is needed
   * as it will wait for the page context to be ready.
   */
  getPageContextForPageSync(pageId: string): PageContext | undefined {
    let pageContext: PageContext | undefined;
    let valueRead = false;

    // Try to get the value synchronously
    // If it fails to emit instantly, ignore any further values.
    const subscription = this.getPageContextForPage(pageId).subscribe((context) => {
      pageContext = context;
      valueRead = true;
    });

    // Unsubscribe instantly - we can't wait here as the read should be synchronous.
    subscription.unsubscribe();

    // If the value was not read, return undefined
    if (!valueRead) {
      logPageContext('info', 'PageContextFacade', 'getPageContextForPage', (tag) =>
        console.log(
          `${tag} 📄 Page Context - pageId: ${pageId}, failed to read value synchronously!`,
        ),
      );
    }

    // Return whatever we got
    return pageContext;
  }

  /**
   * Removes a page context entry from the state.
   * This is used when the page context is no longer needed.
   * For example, when the dialog/drawer is closed and we are sure it cannot be reopened.
   * We don't remove it for Pages as the user can use back/forward navigation.
   */
  removePageContext(pageContextId: string): void {
    this.pageContextCommandService.removePageContext(pageContextId);
  }

  /**
   * Returns the current page context ID url query param value synchronously.
   */
  getCurrentPageContextIdSync(): string | null {
    return this.pageContextForPageService.getCurrentPageContextIdSync();
  }

  /**
   * This is called when exiting a project in the editor.
   */
  dispose(): void {
    this.initializePageContextService.disposePageContext();
  }
}
